import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, CheckCircle, ChevronDown, ExternalLink, Stethoscope } from 'lucide-react';
import { fetchWithFallback } from '../lib/dataFetcher';

/**
 * Renders Lyme, ME/CFS, fibromyalgia, mold/CIRS cards.
 *
 * Props:
 *  - title (string)        : heading text
 *  - subtitle (string)     : muted description below the title
 *  - eyebrow (string)      : small pill label above the title
 *  - accent ('rose'|'purple'): card header gradient
 */
const ACCENT = {
  rose: {
    pill: 'bg-rose-100 text-rose-700',
    header: 'from-rose-500 to-pink-600',
    chip: 'bg-rose-50 text-rose-700',
    btn: 'text-rose-600 hover:text-rose-700',
    iconColor: 'text-rose-500',
  },
  purple: {
    pill: 'bg-purple-100 text-purple-700',
    header: 'from-purple-600 to-violet-700',
    chip: 'bg-purple-50 text-purple-700',
    btn: 'text-purple-600 hover:text-purple-700',
    iconColor: 'text-purple-500',
  },
};

export const ComplexConditionsSection = ({
  title = 'Complex & Emerging Conditions',
  subtitle = 'Disease patterns & stats for conditions that are often misdiagnosed or under-studied — Lyme, ME/CFS, fibromyalgia, and mold/CIRS.',
  eyebrow = 'Under-Recognized Conditions',
  accent = 'rose',
}) => {
  const [conditions, setConditions] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const a = ACCENT[accent] || ACCENT.rose;

  useEffect(() => {
    fetchWithFallback('/api/complex-conditions')
      .then((d) => setConditions(d.conditions || []))
      .catch(() => {});
  }, []);

  if (!conditions.length) return null;

  return (
    <section id="complex-conditions" className="scroll-mt-20" data-testid="complex-conditions-section">
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="text-center mb-6"
      >
        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold mb-3 ${a.pill}`}>
          <Stethoscope className="w-4 h-4" /> {eyebrow}
        </div>
        <h2 className="text-2xl font-bold text-slate-900">{title}</h2>
        <p className="text-slate-500 mt-1 max-w-2xl mx-auto">{subtitle}</p>
      </motion.div>

      <div className="grid gap-4 sm:grid-cols-2">
        {conditions.map((c, i) => {
          const isOpen = expanded === c.id;
          return (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="rounded-2xl bg-white border border-slate-200 overflow-hidden hover:shadow-lg transition-all"
              data-testid={`complex-condition-${c.id}`}
            >
              <div className={`bg-gradient-to-r ${a.header} p-4`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-white text-base leading-tight">{c.name}</h3>
                    {c.long_name && <p className="text-xs text-white/80 truncate">{c.long_name}</p>}
                  </div>
                </div>
              </div>

              <div className="p-4 space-y-3">
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold inline-block ${a.chip}`}>
                  {c.category}
                </span>
                <p className="text-sm text-slate-700 leading-relaxed">{c.tagline}</p>

                {c.prevalence_stat && (
                  <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                    <div className="text-xs text-slate-500">{c.prevalence_stat.label}</div>
                    <div className="text-lg font-bold text-slate-900">
                      {typeof c.prevalence_stat.value === 'number'
                        ? c.prevalence_stat.value.toLocaleString()
                        : c.prevalence_stat.value}
                      <span className="text-sm font-semibold text-slate-600 ml-1">{c.prevalence_stat.suffix}</span>
                    </div>
                  </div>
                )}

                <button
                  onClick={() => setExpanded(isOpen ? null : c.id)}
                  className={`w-full text-xs font-semibold flex items-center justify-between ${a.btn}`}
                  data-testid={`complex-condition-toggle-${c.id}`}
                >
                  {isOpen ? 'Hide details' : 'Show key stats, symptoms & sources'}
                  <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>

                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="space-y-3 overflow-hidden"
                    >
                      {c.key_stats && (
                        <div>
                          <div className="text-xs font-bold text-slate-700 uppercase tracking-wide mb-1">Key stats</div>
                          <ul className="space-y-1">
                            {c.key_stats.map((s, j) => (
                              <li key={j} className="flex items-start gap-2 text-xs text-slate-600">
                                <CheckCircle className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${a.iconColor}`} />
                                <span>{s}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {c.common_symptoms && (
                        <div>
                          <div className="text-xs font-bold text-slate-700 uppercase tracking-wide mb-1">Common symptoms</div>
                          <ul className="space-y-1">
                            {c.common_symptoms.map((s, j) => (
                              <li key={j} className="text-xs text-slate-600">• {s}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {c.what_we_know && (
                        <div className="text-xs text-slate-600">
                          <span className="font-bold text-slate-700">What we know: </span>{c.what_we_know}
                        </div>
                      )}
                      {c.what_we_dont_know && (
                        <div className="text-xs text-slate-600">
                          <span className="font-bold text-slate-700">What we don&apos;t know: </span>{c.what_we_dont_know}
                        </div>
                      )}
                      {c.source_url && (
                        <a
                          href={c.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`text-xs font-semibold inline-flex items-center gap-1 hover:underline ${a.btn}`}
                          data-testid={`complex-condition-source-${c.id}`}
                        >
                          {c.source || 'Source'} <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
};

export default ComplexConditionsSection;
